import { useMemo } from 'react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useEventConditionNameSearch } from '@/services/db/event-name/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import {
  EventRow as SharedEventRow,
  escapedEventNameOptions,
  mergeEventOptions,
  type EventNameOption,
} from '@/features/dryad';
import { ConditionType } from './condition-types';
import { ConditionTypeSelect } from './condition-type-select';

type EventRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
};

/**
 * This surface's event-name options: the environment's bounded `sent_events`
 * as the local seed, merged with debounced search over the workspace's event
 * names. Escaped, because a stored name may contain wildcard characters.
 */
function usePeopleEventNameOptions(term: string): EventNameOption[] {
  const workspaceId = requireWorkspaceId();
  const environment = useEnvironmentOptional();
  const [debouncedTerm] = useDebouncedValue(term, 200);
  const search = useEventConditionNameSearch(workspaceId, debouncedTerm);

  return useMemo(() => {
    const local = escapedEventNameOptions(environment.data?.environment?.sent_events ?? []);
    const remote = escapedEventNameOptions(search.data ?? []);
    return mergeEventOptions(local, remote, term);
  }, [environment.data, search.data, term]);
}

/** The people surface's binding of the shared behavioral-event row. */
export function EventRow({ id }: EventRowProps) {
  return (
    <SharedEventRow
      id={id}
      conditionType={ConditionType.Event}
      typeSelect={<ConditionTypeSelect nodeId={id} currentType={ConditionType.Event} />}
      useEventNameOptions={usePeopleEventNameOptions}
    />
  );
}
